import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, DataGrid } from '@ibsys/kit';
import type { DataGridLeafColumn } from '@ibsys/kit';
import { ConsultantStabilityItem, StabilityZoneCode } from '@/entities/dashboards';
import { cn, zoneBadgeClass } from '@/shared/lib';
import { EmptyState } from './parts/EmptyState';
import { dashboardGridHeight, fmt, stabilityZoneColor } from './utils';

type Props = {
  items: ConsultantStabilityItem[];
};

/** Порядок зон в сводке-счётчиках: от стабильных к нестабильным. */
const ZONE_ORDER: StabilityZoneCode[] = ['stable', 'moderate', 'unstable'];

/**
 * Карточка «Стабильность консультантов»: таблица разброса (σ) общего балла с
 * окраской по зоне стабильности и сводка-счётчики по зонам. Порядок строк —
 * как пришёл с бэкенда (наименее стабильные сверху); клиент не пересортировывает.
 */
export const ConsultantStabilityCard: FC<Props> = ({ items }) => {
  const { t } = useTranslation('qualityDashboard');

  const zoneLabel = useMemo(
    (): Record<StabilityZoneCode, string> => ({
      stable: t('stability.zones.stable'),
      moderate: t('stability.zones.moderate'),
      unstable: t('stability.zones.unstable'),
    }),
    [t],
  );

  const summaryLabel = useMemo(
    (): Record<StabilityZoneCode, string> => ({
      stable: t('stability.summary.stable'),
      moderate: t('stability.summary.moderate'),
      unstable: t('stability.summary.unstable'),
    }),
    [t],
  );

  const counts = useMemo(() => {
    const acc: Record<StabilityZoneCode, number> = { stable: 0, moderate: 0, unstable: 0 };
    for (const item of items) acc[item.zone] += 1;
    return acc;
  }, [items]);

  const columns = useMemo(
    (): DataGridLeafColumn<ConsultantStabilityItem>[] => [
      {
        id: 'consultant',
        header: t('stability.columns.consultant'),
        accessor: 'name',
        size: 200,
      },
      {
        id: 'sigma',
        header: t('stability.columns.sigma'),
        accessor: 'sigma',
        size: 120,
        align: 'center',
        className: 'font-bold',
        cellClassName: ({ row }) => zoneBadgeClass[stabilityZoneColor[row.zone]],
        cell: ({ value }) => fmt(value as number, 2),
      },
      {
        id: 'zone',
        header: t('stability.columns.zone'),
        accessor: 'zone',
        size: 140,
        align: 'center',
        cell: ({ row }) => zoneLabel[row.zone],
      },
    ],
    [t, zoneLabel],
  );

  const tableHeight = dashboardGridHeight(items.length);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('stability.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {ZONE_ORDER.map((zone) => (
                <span
                  key={zone}
                  className={cn('rounded-full px-3 py-1 text-xs font-bold', zoneBadgeClass[stabilityZoneColor[zone]])}
                >
                  {summaryLabel[zone]}: {counts[zone]}
                </span>
              ))}
            </div>
            <DataGrid
              columns={columns}
              dataSource={{ mode: 'client', data: items }}
              getRowId={(r) => r.name}
              enableColumnResizing
              height={tableHeight}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
